import ApiService from "@/core/services/api.service.js";
import { accessibleAimeMenus, contextualAimeMenus, searchAimeMenus } from "@/core/services/aime-menu-catalog.js";
import { guideSource, loadAimeKnowledge, searchGuide } from "@/core/services/aime-knowledge.service.js";

const ARTICLE_ROUTES = {
  dashboard: ["/dashboard"], patients: ["/patient/list"], appointments: ["/appointment/list/0", "/appointment/calendar"],
  queue: ["/queue"], "inpatient-homecare": ["/home-care", "/inpatient"], "medical-record": ["/patient/list"],
  actions: ["/medical-actions/dashboard", "/medical-actions/list"], laboratory: ["/lab-operations", "/lab-procedure/list"],
  quality: ["/quality"], beds: ["/bed/list", "/inpatient"], pharmacy: ["/pharmacy/control-center", "/pharmacy/sales"],
  billing: ["/payment/list"], inventory: ["/inventory/list", "/medicine/control-center"], products: ["/payment-categories/list"],
  finance: ["/finance"], hr: ["/human-resource/doctor-center"], crm: ["/crm", "/efectivity-dashboard"], reports: ["/report"],
  bpjs: ["/bpjs"], satusehat: ["/satu-sehat/dashboard"], "user-access": ["/user-managements/user-access"],
  settings: ["/settings/quick-access"], dental: ["/dental"], aesthetic: ["/aesthetic"], maternal: ["/maternal"],
  mcu: ["/medical-checkup"], eye: ["/eye"], physio: ["/therapy"], occupational: ["/occupational-health"], vaccination: ["/vaccination"],
};

const ANALYTICS_ROUTES = {
  revenue: ["/payment/list", "/report"],
  stock: ["/medicine/control-center", "/medicine/stock", "/inventory/list"],
  queue: ["/queue", "/appointment/todays"],
};

const SAFE_REFUSAL = "Saya tidak dapat menentukan diagnosis, obat, resep, dosis, atau terapi untuk pasien. AIME saat ini hanya membantu panduan operasional Medhop dan ringkasan data operasional tanpa identitas pasien. Untuk keputusan klinis, gunakan RME dan tenaga kesehatan berwenang.";

function asksForClinicalDecision(question) {
  return /(?:diagnosis|penyakit)\s+(?:apa|pasien|pasti)|(?:obat|terapi)\s+apa|(?:dosis|takaran)\s+(?:berapa|yang tepat)|(?:buatkan|berikan|tentukan|resepkan)\s+(?:resep|diagnosis|obat|terapi)/i.test(question);
}

function analyticsIntent(question) {
  if (/omzet|pendapatan|revenue|transaksi hari ini/i.test(question)) return "revenue";
  if (/stok|batch|kedaluwarsa|expired/i.test(question)) return "stock";
  if (/antrean|antrian|queue|menunggu|dilayani/i.test(question)) return "queue";
  return null;
}

function formatCurrency(value) {
  return `Rp ${Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;
}

function analyticsAnswer(snapshot, intent) {
  const data = snapshot.snapshot || {};
  if (intent === "revenue" && data.revenue_today) {
    return `Omzet tercatat hari ini ${formatCurrency(data.revenue_today.amount)} dari ${data.revenue_today.transaction_count} transaksi. Angka mengikuti cabang aktif dan hak akses Anda.`;
  }
  if (intent === "stock" && data.stock) {
    return `Ringkasan stok: ${data.stock.expired_batches} batch kedaluwarsa masih bersaldo, ${data.stock.expiring_30_days} batch akan kedaluwarsa dalam 30 hari, dan ${data.stock.empty_batches} batch kosong.`;
  }
  if (intent === "queue" && data.queue_today) {
    return `Antrean hari ini: ${data.queue_today.waiting} menunggu, ${data.queue_today.called} dipanggil, ${data.queue_today.serving} sedang dilayani, dan ${data.queue_today.completed} selesai.`;
  }
  return "Data tersebut tidak tersedia untuk hak akses atau konfigurasi cabang Anda saat ini.";
}

function guideAnswer(guide, result) {
  if (result.type === "faq") return result.answer;
  const intro = (guide.introductions || {})[result.id] || result.outcome;
  const steps = (result.steps || []).slice(0, 5).map((step, index) => `${index + 1}. ${step}`).join("\n");
  const reminder = result.watch ? `\n\nPerlu diperhatikan: ${result.watch}` : "";
  return `${intro}\n\nLangkah yang disarankan:\n${steps}${reminder}`;
}

function actionsForArticle(article, menus) {
  const routes = ARTICLE_ROUTES[article.id] || [];
  return routes.map(route => menus.find(menu => menu.route === route)).filter(Boolean).slice(0, 2);
}

function actionsForAnalytics(intent, menus) {
  return (ANALYTICS_ROUTES[intent] || [])
    .map(route => menus.find(menu => menu.route === route))
    .filter(Boolean)
    .slice(0, 2);
}

async function sha256(value) {
  if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) return null;
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, "0")).join("");
}

async function audit(question, answer, currentPage, guideVersion) {
  try {
    const questionHash = await sha256(question);
    if (!questionHash) return;
    await ApiService.post("aime/operational-events", {
      question_hash: questionHash,
      result_type: answer.type,
      source_ids: (answer.sources || []).map(source => source.id).filter(Boolean).slice(0, 10),
      suggested_routes: (answer.actions || []).map(action => action.route).slice(0, 5),
      current_page: String(currentPage || "/").split("#")[0],
      guide_version: guideVersion || null,
    });
  } catch (_) {
    // Telemetry must never prevent the user from receiving operational help.
  }
}

const AimeService = {
  async initialize() {
    const state = { guide: null, context: null };
    const results = await Promise.all([
      loadAimeKnowledge().catch(() => null),
      ApiService.get("aime/operational-context").catch(() => null),
    ]);
    state.guide = results[0];
    if (results[1]) state.context = results[1].data.data;
    return state;
  },

  contextualMenus(router, currentPath, context) {
    return contextualAimeMenus(router, currentPath, context && context.effective_access_codes);
  },

  async answer(question, currentPage, router, state) {
    const guide = state.guide;
    const context = state.context;
    const accessCodes = context && context.effective_access_codes;
    const menus = accessibleAimeMenus(router, accessCodes);
    let answer;

    if (asksForClinicalDecision(question)) {
      answer = { text: SAFE_REFUSAL, sources: [], actions: [], type: "safety_refusal" };
    } else {
      const insight = analyticsIntent(question);
      if (insight) {
        try {
          const response = await ApiService.get("aime/operational-snapshot");
          const snapshot = response.data.data;
          answer = {
            text: analyticsAnswer(snapshot, insight),
            sources: (snapshot.sources || [])
              .filter(id => ({ revenue: "payments", stock: "medicine_batch_stocks", queue: "clinic_queues" })[insight] === id)
              .map(id => ({ id, label: `Data ${id} tenant aktif`, kind: "tenant_aggregate" })),
            actions: actionsForAnalytics(insight, menus),
            type: "analytics",
          };
        } catch (_) {
          answer = { text: "Ringkasan operasional belum dapat dimuat. Periksa cabang aktif dan hak akses Anda, lalu coba kembali.", sources: [], actions: [], type: "no_match" };
        }
      } else {
        const guideResults = guide ? searchGuide(guide, question, 3) : [];
        const menuResults = searchAimeMenus(router, question, accessCodes, 3);
        if (guideResults.length) {
          const best = guideResults[0];
          answer = {
            text: guideAnswer(guide, best),
            sources: guideResults.slice(0, 2).map(item => guideSource(guide, item)),
            actions: actionsForArticle(best, menus).concat(menuResults).filter((item, index, all) => all.findIndex(other => other.route === item.route) === index).slice(0, 3),
            type: "user_guide",
          };
        } else if (menuResults.length) {
          answer = {
            text: `Menu yang paling sesuai dengan akses Anda: ${menuResults.map(item => item.label).join(", ")}.`,
            sources: [], actions: menuResults, type: "menu",
          };
        } else {
          answer = {
            text: "Saya belum menemukan panduan atau menu yang cocok. Coba sebutkan prosesnya, misalnya daftar pasien, antrean, farmasi, pembayaran, laporan, BPJS, atau pengaturan akses.",
            sources: guide ? [{ id: "guide", label: "Buka seluruh User Guide Medhop", url: "/guide/index.html", kind: "user_guide", version: guide.version }] : [],
            actions: this.contextualMenus(router, currentPage, context), type: "no_match",
          };
        }
      }
    }

    audit(question, answer, currentPage, guide && guide.version);
    return answer;
  },
};

export default AimeService;
